import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Globe, Lock, Loader2, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";

const MAX_PROMPT_LENGTH = 10000;

type AiHousePromptResponse = {
  effectivePrompt: string;
  isDefault: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  lockedRulesText: string;
  defaultPrompt: string;
};

export function AiReplyVoicePanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [prompt, setPrompt] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  const settingsQuery = useQuery<AiHousePromptResponse>({
    queryKey: ["/api/app/admin/ai-house-prompt"],
    queryFn: async () => {
      const res = await fetch("/api/app/admin/ai-house-prompt", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load AI reply voice");
      return res.json();
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      setPrompt(settingsQuery.data.effectivePrompt ?? "");
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (next: string) =>
      apiRequest("PUT", "/api/app/admin/ai-house-prompt", { prompt: next }),
    onSuccess: async (res) => {
      const data = (await res.json()) as AiHousePromptResponse;
      setPrompt(data.effectivePrompt ?? "");
      queryClient.setQueryData(["/api/app/admin/ai-house-prompt"], data);
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/ai-house-prompt"] });
      toast({
        title: "AI reply voice saved",
        description: "The new house voice now applies to every customer org immediately.",
      });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to save",
        description: parseErrorMessage(err, "Couldn't save the AI reply voice. Please try again."),
        variant: "destructive",
      }),
  });

  const resetMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/app/admin/ai-house-prompt/reset", {}),
    onSuccess: async (res) => {
      const data = (await res.json()) as AiHousePromptResponse;
      setPrompt(data.effectivePrompt ?? "");
      queryClient.setQueryData(["/api/app/admin/ai-house-prompt"], data);
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/ai-house-prompt"] });
      toast({
        title: "Reset to default",
        description: "The AI reply voice has been restored to the seeded default.",
      });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to reset",
        description: parseErrorMessage(err, "Couldn't reset the AI reply voice. Please try again."),
        variant: "destructive",
      }),
  });

  const data = settingsQuery.data;
  const isDefault = data?.isDefault === true;
  const updatedBy = data?.updatedBy ?? null;
  const updatedAt = data?.updatedAt ?? null;
  const dirty = initialized && prompt !== (data?.effectivePrompt ?? "");
  const busy = saveMutation.isPending || resetMutation.isPending;

  return (
    <div className="max-w-3xl space-y-4">
      <Card className="p-5 space-y-5">
        <div>
          <h3 className="font-semibold text-sm mb-1 flex items-center gap-1.5" data-testid="text-ai-reply-voice-title">
            <Globe className="h-4 w-4 text-brand" />
            AI Reply Voice &amp; Service Standard
          </h3>
          <p className="text-xs text-muted-foreground">
            This is the platform-wide tone and service philosophy used by AI Suggest and Improve. It applies to{" "}
            <span className="font-medium text-foreground">every customer org</span> — one voice across the whole
            platform — and takes effect immediately, no redeploy required. Each org's own company knowledge base,
            property knowledge base, and approved memory stay private to that org; only this tone wrapper is global.
          </p>
        </div>

        {/* Read-only locked safety contract — applied first on every call, cannot be edited here. */}
        <div className="rounded-md border bg-muted/40 px-3 py-2.5 space-y-1.5" data-testid="section-ai-locked-rules">
          <button
            type="button"
            onClick={() => setRulesOpen((o) => !o)}
            className="w-full flex items-center gap-1.5 text-xs text-muted-foreground"
            aria-expanded={rulesOpen}
            data-testid="button-toggle-ai-locked-rules"
          >
            {rulesOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
            <Lock className="h-3.5 w-3.5 shrink-0" />
            <span className="text-left">Always-on safety rules (locked — applied before your voice, cannot be changed)</span>
          </button>
          {rulesOpen && (
            <pre
              className="text-[11px] whitespace-pre-wrap font-sans text-muted-foreground leading-relaxed pt-1"
              data-testid="text-ai-locked-rules"
            >
              {settingsQuery.isLoading ? "" : data?.lockedRulesText}
            </pre>
          )}
        </div>

        {settingsQuery.isLoading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm" htmlFor="ai-house-voice">
                House voice &amp; service standard
              </Label>
              <Textarea
                id="ai-house-voice"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={18}
                maxLength={MAX_PROMPT_LENGTH}
                className="text-xs font-mono leading-relaxed"
                data-testid="textarea-ai-house-voice"
              />
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span data-testid="text-ai-house-voice-status">
                  {isDefault ? "Currently using the seeded default voice." : "Using a custom voice."}
                  {updatedBy ? ` Last changed by ${updatedBy}${updatedAt ? ` on ${new Date(updatedAt).toLocaleString()}` : ""}.` : ""}
                </span>
                <span>{prompt.length.toLocaleString()}/{MAX_PROMPT_LENGTH.toLocaleString()}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Leave this empty and save to fall back to the seeded default voice.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                className="flex-1"
                onClick={() => saveMutation.mutate(prompt)}
                disabled={busy || !dirty}
                data-testid="button-save-ai-house-voice"
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Voice
              </Button>
              <Button
                variant="outline"
                onClick={() => resetMutation.mutate()}
                disabled={busy || isDefault}
                data-testid="button-reset-ai-house-voice"
              >
                {resetMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Reset to Default
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
